import React, { useState } from 'react';
import { X, Plus, Minus, Trash2, ShoppingBag, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const Cart = () => {
  const { cartItems, removeFromCart, updateQuantity, getCartTotal, isCartOpen, setIsCartOpen, clearCart } = useCart();
  const { user } = useAuth();
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID;

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleCheckout = async () => {
    if (cartItems.length === 0) {
      toast({
        title: 'Cart is empty',
        description: 'Please add items to your cart before checkout.',
        variant: 'destructive',
      });
      return;
    }

    setIsProcessing(true);

    try {
      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        throw new Error('Failed to load Razorpay SDK');
      }

      // Prepare cart items for API
      const items = cartItems.map(item => ({
        id: item.id,
        name: item.name,
        price: item.salePrice,
        quantity: item.quantity
      }));

      // Create order on backend (attach logged-in user if any)
      const response = await axios.post(`${API_URL}/api/create-order`, {
        items: items,
        customer_name: user?.name || null,
        customer_email: user?.email || null
      }, { withCredentials: true });

      const { order_id, amount, currency, key_id } = response.data;

      // Configure Razorpay options
      const options = {
        key: key_id || RAZORPAY_KEY_ID,
        amount: amount,
        currency: currency,
        name: 'Harpalyk',
        description: `Order for ${cartItems.length} item(s)`,
        order_id: order_id,
        handler: async function (response) {
          try {
            // Verify payment on backend
            await axios.post(`${API_URL}/api/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });

            toast({
              title: 'Payment Successful! 🎉',
              description: user ? 'Track your order anytime in My Account → Order History.' : 'Thank you for your order. You will receive a confirmation email shortly.',
            });

            // Clear cart after successful payment
            clearCart();
            setIsCartOpen(false);

          } catch (verifyError) {
            console.error('Payment verification failed:', verifyError);
            toast({
              title: 'Payment Verification Failed',
              description: 'Please contact support with your payment ID.',
              variant: 'destructive',
            });
          }
        },
        prefill: {
          name: user?.name || '',
          email: user?.email || '',
          contact: ''
        },
        notes: {
          items_count: cartItems.length,
          total_amount: getCartTotal()
        },
        theme: {
          color: '#10b981'
        },
        modal: {
          ondismiss: function() {
            setIsProcessing(false);
            toast({
              title: 'Payment Cancelled',
              description: 'You can complete your purchase anytime.',
            });
          }
        }
      };

      // Open Razorpay checkout
      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', function (response) {
        toast({
          title: 'Payment Failed',
          description: response.error.description || 'Please try again.',
          variant: 'destructive',
        });
        setIsProcessing(false);
      });
      razorpay.open();

    } catch (error) {
      console.error('Checkout error:', error);
      toast({
        title: 'Checkout Failed',
        description: error.response?.data?.detail || 'Unable to process checkout. Please try again.',
        variant: 'destructive',
      });
      setIsProcessing(false);
    }
  };

  if (!isCartOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-50 transition-opacity duration-300"
        onClick={() => setIsCartOpen(false)}
      />

      {/* Cart Sidebar */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-50 flex flex-col animate-in slide-in-from-right duration-300">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <ShoppingBag className="text-gray-900" size={24} />
            <h2 className="text-2xl font-light text-gray-900">Shopping Cart</h2>
          </div>
          <button
            onClick={() => setIsCartOpen(false)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
            data-testid="close-cart-btn"
          >
            <X size={24} className="text-gray-600" />
          </button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-6">
          {cartItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <ShoppingBag size={64} className="text-gray-300 mb-4" />
              <p className="text-xl font-light text-gray-600 mb-2">Your cart is empty</p>
              <p className="text-gray-500 mb-6">Add products to get started</p>
              <Button
                onClick={() => setIsCartOpen(false)}
                className="bg-gray-900 text-white hover:bg-gray-800"
              >
                Continue Shopping
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {cartItems.map((item) => (
                <div key={item.id} className="flex gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
                  {/* Product Image */}
                  <div className="w-20 h-20 rounded-lg overflow-hidden bg-white flex-shrink-0">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Product Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div>
                        <h3 className="text-sm font-medium text-gray-900 truncate">{item.name}</h3>
                        <Badge variant="outline" className="mt-1 text-xs">
                          {item.category}
                        </Badge>
                      </div>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="p-1 hover:bg-gray-200 rounded transition-colors duration-200"
                        data-testid={`remove-item-${item.id}`}
                      >
                        <Trash2 size={16} className="text-red-500" />
                      </button>
                    </div>

                    <div className="flex items-center justify-between mt-3">
                      {/* Quantity Controls */}
                      <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="p-2 hover:bg-gray-100 rounded-l-lg transition-colors duration-200"
                          data-testid={`decrease-qty-${item.id}`}
                        >
                          <Minus size={14} className="text-gray-600" />
                        </button>
                        <span className="px-3 text-sm font-medium text-gray-900">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="p-2 hover:bg-gray-100 rounded-r-lg transition-colors duration-200"
                          data-testid={`increase-qty-${item.id}`}
                        >
                          <Plus size={14} className="text-gray-600" />
                        </button>
                      </div>

                      {/* Price */}
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900">
                          {formatPrice(item.salePrice * item.quantity)}
                        </p>
                        {item.quantity > 1 && (
                          <p className="text-xs text-gray-500">
                            {formatPrice(item.salePrice)} each
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Clear Cart Button */}
              {cartItems.length > 0 && (
                <Button
                  variant="outline"
                  onClick={clearCart}
                  className="w-full border-gray-300 text-gray-700 hover:bg-gray-100"
                  data-testid="clear-cart-btn"
                >
                  <Trash2 size={16} className="mr-2" />
                  Clear Cart
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {cartItems.length > 0 && (
          <div className="border-t border-gray-200 p-6 space-y-4 bg-gray-50">
            {/* Subtotal */}
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Subtotal</span>
              <span className="text-2xl font-light text-gray-900">{formatPrice(getCartTotal())}</span>
            </div>

            {/* Info */}
            <p className="text-xs text-gray-500 text-center">
              Shipping and taxes calculated during checkout
            </p>

            {/* Checkout Button */}
            <Button
              onClick={handleCheckout}
              disabled={isProcessing}
              className="w-full bg-gray-900 text-white hover:bg-gray-800 py-6 text-lg group transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="checkout-btn"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  Proceed to Checkout
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" size={20} />
                </>
              )}
            </Button>

            <p className="text-xs text-gray-500 text-center">
              Secure payment powered by Razorpay
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default Cart;
